import React, { useState } from 'react';
import { Box, TextField, InputAdornment, Autocomplete } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useKBTheme } from '../hooks/useKBTheme';
import { useNavigate } from 'react-router-dom';

interface KBHeaderProps {
  onSearch: (query: string) => void;
  searchSuggestions?: string[];
}

export const KBHeader: React.FC<KBHeaderProps> = ({ onSearch, searchSuggestions = [] }) => {
  const { mode } = useKBTheme();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    if (query.trim()) {
      onSearch(query);
      navigate(`/knowledge-base/search?q=${encodeURIComponent(query)}`);
    }
  };

  return (
    <Box
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 1000,
        background: mode === 'dark'
          ? 'rgba(15, 23, 42, 0.95)'
          : 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(20px)',
        borderBottom: mode === 'dark'
          ? '1px solid rgba(71, 85, 105, 0.4)'
          : '1px solid rgba(226, 232, 240, 0.8)',
        py: 2,
        px: 3,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, maxWidth: '1400px', mx: 'auto' }}>
        <Box
          onClick={() => navigate('/knowledge-base')}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            cursor: 'pointer',
            '&:hover': { opacity: 0.8 },
          }}
        >
          <Box
            component="img"
            src="/atlasai-icon.svg"
            alt="CoreSupport"
            sx={{ width: 40, height: 40 }}
          />
          <Box>
            <Box
              sx={{
                fontSize: '1.5rem',
                fontWeight: 700,
                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #ec4899 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Arial, sans-serif',
              }}
            >
              CoreSupport
            </Box>
            <Box
              sx={{
                fontSize: '0.75rem',
                fontWeight: 500,
                color: mode === 'dark' ? 'rgba(255, 255, 255, 0.6)' : '#64748b',
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
              }}
            >
              Knowledge Base
            </Box>
          </Box>
        </Box>

        <Box sx={{ flex: 1, maxWidth: 600 }}>
          <Autocomplete
            freeSolo
            options={searchSuggestions}
            onInputChange={(_, value) => {
              if (value) handleSearch(value);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Search knowledge base..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleSearch(searchQuery);
                  }
                }}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: mode === 'dark' ? 'rgba(255,255,255,0.5)' : '#94a3b8' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    background: mode === 'dark' ? 'rgba(71, 85, 105, 0.3)' : 'rgba(241, 245, 249, 0.8)',
                    borderRadius: 3,
                    '&:hover': {
                      background: mode === 'dark' ? 'rgba(71, 85, 105, 0.5)' : 'rgba(241, 245, 249, 1)',
                    },
                    '&.Mui-focused': {
                      background: mode === 'dark' ? 'rgba(71, 85, 105, 0.6)' : 'rgba(255, 255, 255, 1)',
                    },
                  },
                }}
              />
            )}
          />
        </Box>
      </Box>
    </Box>
  );
};








